import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumCurrencyFormatter } from '@wix/premium-react-text';
import { CycleMultiplier } from '../../../constants';
import s from './Cycles.scss';
import { PaymentCycle, Country } from 'enums';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { RootStore, withInjectedRootStore } from 'providers';
import { isCycleOfMonths } from 'utils';
import { Text } from 'wix-style-react';
import { CycleDetails } from 'types';
import { FullPrice } from './MonthlyFullPrice';

export interface CyclePriceProps extends InjectedExperimentsProps {
  country: Country;
  isInSale: boolean;
  rootStore: RootStore;
  cycleDetails: CycleDetails;
}

const CyclePriceComp: React.FunctionComponent<CyclePriceProps> = ({
  country,
  isInSale,
  rootStore,
  cycleDetails,
}) => {
  const {
    fullTermDisplayPrice: fullPrice,
    monthlyFullPrice,
    fullTermActualPrice: actualPrice,
    cycleId,
    firstPeriodPriceMonthly,
  } = cycleDetails;

  const {
    isSaleProduct,
    appContext: { isLimitedTimeDiscount },
  } = rootStore.appStore;

  const isBrazil = country === Country.BR;

  const priceTextWeight = !isBrazil && isInSale ? 'normal' : 'thin';

  const sholdShowOriginalPrice =
    isBrazil && isInSale && fullPrice !== actualPrice;

  const sholdShowBrazilPrice = isBrazil && cycleId !== PaymentCycle.MONTHLY;

  const isLimitedTimeOfferingMode =
    isLimitedTimeDiscount && !!firstPeriodPriceMonthly && !isBrazil;

  const isInSaleMode = isSaleProduct && !isBrazil;

  const shouldShowMonthlyFullPrice = isInSaleMode || isLimitedTimeOfferingMode;
  const monthlyFullPriceClassname = !isBrazil ? s.cycleSaleFullpriceText : '';

  return (
    <Text
      weight={priceTextWeight}
      className={s.cycleEntryPrice}
      size='medium'
      dataHook='cycle-price'
    >
      {sholdShowOriginalPrice && <FullPrice.BrazilSale fullPrice={fullPrice} />}
      {sholdShowBrazilPrice && <FullPrice.Brazil actualPrice={actualPrice} />}
      {shouldShowMonthlyFullPrice && !isCycleOfMonths(cycleId) && (
        <FullPrice.Monthly
          monthlyFullPrice={monthlyFullPrice}
          className={monthlyFullPriceClassname}
        />
      )}
      <CycleMultiplierText cycleDetails={cycleDetails} country={country} />
    </Text>
  );
};

const CycleMultiplierText: React.FC<{
  cycleDetails: CycleDetails;
  country: Country;
}> = ({
  cycleDetails: { cycleId, monthlyActualPrice, firstPeriodPriceMonthly },
  country,
}) => {
  const { t } = useTranslation();
  const isBrazil = country === Country.BR;
  const cyclePriceKey =
    cycleId === PaymentCycle.MONTHLY
      ? 'cycle_entry.cycle_price.MONTHLY'
      : isBrazil
      ? 'cycle_entry.cycle_price.br'
      : 'cycle_entry.cycle_price';

  return (
    <PremiumCurrencyFormatter
      render={(formatCurrency) =>
        t(cyclePriceKey, {
          price: formatCurrency(firstPeriodPriceMonthly || monthlyActualPrice),
          cycleLength: CycleMultiplier[cycleId],
        })
      }
    />
  );
};

export const CyclePrice = withInjectedRootStore(
  withExperiments(CyclePriceComp),
);
